import { useEffect, type ReactNode } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { PixelIcon } from './PixelIcon'

interface ModalProps {
  open: boolean
  onClose?: () => void
  title?: string
  children: ReactNode
  dismissable?: boolean
  maxWidth?: number
}

export function Modal({ open, onClose, title, children, dismissable = true, maxWidth = 460 }: ModalProps) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissable) onClose?.()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, dismissable, onClose])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{ zIndex: 'var(--z-modal)' as unknown as number }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => dismissable && onClose?.()}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.92, y: 12, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 380, damping: 30 }}
            style={{ maxWidth, width: '100%' }}
            className="relative rounded-[var(--r-xl)] bg-[var(--surface)] shadow-raised border border-[var(--edge)]/50 p-6"
          >
            {(title || (dismissable && onClose)) && (
              <div className="flex items-center justify-between mb-4">
                {title && <h2 className="font-display text-xl font-bold text-[var(--text)]">{title}</h2>}
                {dismissable && onClose && (
                  <button
                    aria-label="Close"
                    onClick={onClose}
                    className="ml-auto grid place-items-center w-10 h-10 rounded-full bg-[var(--surface-raised)] text-[var(--text-dim)] hover:text-[var(--text)]"
                  >
                    <PixelIcon name="close" size={18} />
                  </button>
                )}
              </div>
            )}
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
